/**
 * Policy Management API module - custom policies, versioning, Rego/OPA,
 * policy bundles, and IaC policy scanning.
 *
 * Real per-domain split: contains the actual implementations of the policy
 * management API objects, extracted verbatim from the monolithic
 * frontend/src/services/api.ts as part of the ADR-0003 follow-up.
 *
 * Domain objects:
 *   - customPolicyApi   (CRUD/test/dry-run/templates/impact for custom policies)
 *   - policyVersionApi  (cross-policy version list/get/rollback/diff)
 *   - regoPolicyApi     (Rego/OPA policy CRUD + evaluate + test)
 *   - policyBundlesApi  (CSPM policy bundles - list/toggle/statistics)
 *   - iacPolicyScanApi  (IaC template policy scanning)
 */
import { _unwrap, _thenUnwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';

// -- customPolicyApi --
export const customPolicyApi = {
  create: (data: any) => fetchClient.post('/api/v1/policies/custom', data).then(_thenUnwrap),
  list: (params?: any) => fetchClient.get('/api/v1/policies/custom', params).then(_thenUnwrap),
  get: (id: string) => fetchClient.get(`/api/v1/policies/custom/${id}`).then(_thenUnwrap),
  update: (id: string, data: any) => fetchClient.put(`/api/v1/policies/custom/${id}`, data).then(_thenUnwrap),
  delete: (id: string) => fetchClient.delete(`/api/v1/policies/custom/${id}`).then(_thenUnwrap),
  test: (id: string, resource: any) => fetchClient.post(`/api/v1/policies/custom/${id}/test`, resource).then(_thenUnwrap),
  dryRun: (id: string, resources: any[]) => fetchClient.post(`/api/v1/policies/custom/${id}/dry-run`, { resources }).then(_thenUnwrap),
  getTemplates: () => fetchClient.get('/api/v1/policies/custom/templates').then(_thenUnwrap),
  createFromTemplate: (templateId: string) => fetchClient.post(`/api/v1/policies/custom/from-template/${templateId}`).then(_thenUnwrap),
  getImpact: (id: string) => fetchClient.get(`/api/v1/policies/custom/${id}/impact`).then(_thenUnwrap),
};

// -- policyVersionApi --
export const policyVersionApi = {
  listVersions: async (policyId: string): Promise<any> => _unwrap(await fetchClient.get<any>(`/api/v1/policies/${policyId}/versions`)),
  getVersion: async (policyId: string, version: number): Promise<any> => _unwrap(await fetchClient.get<any>(`/api/v1/policies/${policyId}/versions/${version}`)),
  rollback: async (policyId: string, version: number): Promise<any> => _unwrap(await fetchClient.post<any>(`/api/v1/policies/${policyId}/rollback/${version}`)),
  diff: async (policyId: string, v1: number, v2: number): Promise<any> => _unwrap(await fetchClient.get<any>(`/api/v1/policies/${policyId}/diff/${v1}/${v2}`)),
};

// -- regoPolicyApi --
export const regoPolicyApi = {
  list: () => fetchClient.get('/api/v1/cspm/rego-policies').then(_thenUnwrap),
  create: (data: any) => fetchClient.post('/api/v1/cspm/rego-policies', data).then(_thenUnwrap),
  get: (id: string) => fetchClient.get(`/api/v1/cspm/rego-policies/${id}`).then(_thenUnwrap),
  update: (id: string, data: any) => fetchClient.put(`/api/v1/cspm/rego-policies/${id}`, data).then(_thenUnwrap),
  delete: (id: string) => fetchClient.delete(`/api/v1/cspm/rego-policies/${id}`).then(_thenUnwrap),
  evaluate: (data: { rego_source: string; input: any }) => fetchClient.post('/api/v1/cspm/rego-policies/evaluate', data).then(_thenUnwrap),
  test: (id: string, data: { test_cases: any[] }) => fetchClient.post(`/api/v1/cspm/rego-policies/${id}/test`, data).then(_thenUnwrap),
  getTemplates: () => fetchClient.get('/api/v1/cspm/rego-policies/templates').then(_thenUnwrap),
};

// -- policyBundlesApi --
export const policyBundlesApi = {
  list: () => fetchClient.get('/api/v1/cspm/policies/bundles').then(_thenUnwrap),
  toggle: (id: string) => fetchClient.post(`/api/v1/cspm/policies/bundles/${id}/toggle`).then(_thenUnwrap),
  getStatistics: () => fetchClient.get('/api/v1/cspm/policies/statistics').then(_thenUnwrap),
};

// -- iacPolicyScanApi --
export const iacPolicyScanApi = {
  scan: (data: { template: string; format: string }) => fetchClient.post('/api/v1/cspm/policies/iac/scan', data).then(_thenUnwrap),
  getRules: () => fetchClient.get('/api/v1/cspm/policies/iac/rules').then(_thenUnwrap),
};
